import React, { FC, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import { replaceDate } from "@/helper/dateHandling";
import { useNavigate } from "react-router-dom";
import { Switch, Tooltip } from "@mui/material";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { KaerIcon } from "@/icons/kaerIcon";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

const ActionChainMainTable: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("actionchain_templates"));
  const [refreshTrigger, setRefreshtrigger] = useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();
  const navigate = useNavigate();
  const { showConfirmationModalAsync } = useConfirmationModal();

  const handleNavigateChainEdit = (row: any) => {
    navigate(`/settings/actionChains/${row.original.id}`);
  };

  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    row: any
  ) => {
    const newTemplateEnabledState = event.target.checked;

    const confirmMessage = newTemplateEnabledState
      ? t("actionchain_setactive_confirm", { title: row.name })
      : t("actionchain_setinactive_confirm", { title: row.name });
    const result = { enabled: newTemplateEnabledState };
    const confirmDelete = await showConfirmationModalAsync(confirmMessage);

    if (!confirmDelete) return;

    api.genericApiRequest({
      method: "put",
      entity: "actionChainTemplate",
      entityId: row.id,
      submitData: result,
      successHandler: () => {
        setRefreshtrigger((prev) => !prev);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const actionChainTemplateColumns = [
    { header: t("id"), accessorKey: "id", id: "id" },
    {
      header: t("name"),
      accessorKey: "name",
      id: "name",
      enableForCreate: true,
    },
    {
      header: t("active"),
      accessorKey: "enabled",
      id: "enabled",
      filterVariant: "checkbox",
      accessorFn: (row: any) => {
        if (row.enabled !== null) {
          if (row.enabled) {
            return t("yes");
          } else {
            return t("no");
          }
        } else return "-";
      },
    },
    {
      header: t("created_by"),
      accessorKey: "created_by",
      id: "created_by",
      customColumnFilterId: "created_by_name",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()?.name}</span>;
      },
    },
    {
      header: t("created_on"),
      accessorKey: "created_on",
      id: "created_on",
      sortingFn: "datetime",
      filterVariant: "date",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
    {
      header: t("modified_by"),
      accessorKey: "modified_by",
      id: "modified_by",
      customColumnFilterId: "modified_by_name",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()?.name}</span>;
      },
    },
    {
      header: t("modified_on"),
      accessorKey: "modified_on",
      id: "modified_on",
      filterVariant: "date",

      sortingFn: "datetime",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
  ];

  const customRowActions = (row: any) => {
    return (
      <>
        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={t("actionchain_edit")}
        >
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() => {
              handleNavigateChainEdit(row);
            }}
          >
            <KaerIcon icon="actionChain" />
          </GenericButton>
        </Tooltip>
        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={
            row.original.enabled
              ? t("actionchain_setinactive")
              : t("actionchain_setactive")
          }
        >
          <Switch
            checked={row.original.enabled}
            onChange={(event: any) => handleChange(event, row.original)}
            inputProps={{ "aria-label": "controlled" }}
          />
        </Tooltip>
      </>
    );
  };

  return (
    <>
      <GenericCrudTable
        columns={actionChainTemplateColumns}
        entity="actionChainTemplate"
        customRowActions={customRowActions}
        refreshTable={refreshTrigger}
        showColumnFilterbyDefault
      />
      {errorModal}
    </>
  );
};

export default ActionChainMainTable;
